/**
 * Prompts at run time (D235, actors round B2) — an actor ASKING, the
 * question parked as the third kind, the player's words read as an answer,
 * and the ledger of what has been asked (`docs/design/actors.md` §4; Mike's
 * rulings 4 and 6 of 2026-10-04).
 *
 * @remarks
 * Three questions, one module, because they are one rule read from three
 * ends. **Whether a prompt is asked:** the actor must be within earshot of
 * the player (`isActorAddressable` — a question nobody can hear is not
 * asked), the prompt must not be spent (asked already and not
 * `repeatable`), and at least one option must be OFFERABLE (its `when`
 * holds, or it has none); otherwise the ask is declined, silently, exactly
 * as a voice line declines (R12). **What asking does:** speaks the
 * `questionText` in the actor's voice under `prompt_asked:<promptId>`,
 * parks a `dialogue` question carrying the offered options — label and
 * every folded word that means it — and writes the prompt into the
 * player's ledger. **What answers it:** the player's input, folded, against
 * each offered option's words, whole, first in declared order; never fuzzy
 * (D27's discipline — an answer that "almost" matched another option is a
 * lie the player did not tell). No match is not an answer at all, and the
 * input runs as the command it is.
 *
 * The answer, once matched, is a TURN (`answerPrompt`): the option's lines
 * under `dialogue:<promptId>:<optionId>`, its effects folded as a topic's
 * are, and — the back-and-forth — the prompt its `leadsTo` names asked in
 * the same turn, through the same door as every other ask. The turn's
 * occurrence is `dialogue_answered`, which the triggers hear with the
 * prompt and the option in hand.
 *
 * The ledger is the player's (ruling 4): `narrative.askedPromptIds`, kept
 * across a checkpoint restore with the spent topics and the voice ledger.
 * The world is put back; what was asked stays asked.
 *
 * @packageDocumentation
 */

import type { ActorDefinition, ActorId, NarrativeLine, PromptDefinition, PromptId, PromptOption, RuntimeState } from '@orpheus/content-schema';

import { foldCharacters } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requirePrompt } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { isActorAddressable } from '../entities/actor-presence.js';
import { type CompletedAction } from '../execution/action-handler-result.js';
import { rememberConversationPartner } from '../execution/conversational-partner.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';
import { parkQuestion, type PendingDialogueQuestion } from '../execution/pending-question.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from '../execution/transcript-cursor.js';
import { withEffects } from '../execution/with-effects.js';
import { resolveConditionalText } from '../narrative/conditional-text.js';

/** What asking did: the lines spoken (the question, or nothing), the cursor after them, and the state with the question parked — or unchanged, declined. */
export interface PromptAsking extends StampedNarratives {
	readonly state: RuntimeState;
}

/**
 * The voice a prompt is asked in — the actor's declared speaker. The schema
 * refuses prompts on a mute actor, so a missing voice here is a corrupt
 * bundle, not a case.
 *
 * @param actor - The actor asking
 * @returns Its speaker id
 */
const voiceOf = (actor: ActorDefinition): NonNullable<ActorDefinition['speakerId']> => {
	if (actor.speakerId === undefined) {
		throw new EngineInvariantError(`actor "${actor.actorId}" asks a prompt and declares no speakerId — the schema should have rejected this bundle`);
	}

	return actor.speakerId;
};

/**
 * The options a prompt OFFERS now — those whose `when` holds, or which have
 * none — in declared order.
 *
 * @param bundle - The loaded bundle
 * @param state - The live state the gates read
 * @param prompt - The prompt
 * @returns The offerable options
 */
export const offerableOptionsOf = (bundle: EngineGameBundle, state: RuntimeState, prompt: PromptDefinition): readonly PromptOption[] =>
	prompt.options.filter((option) => option.when === undefined || evaluateCondition(option.when, bundle, state));

/**
 * Has this prompt been asked and spent — asked already, and not repeatable?
 *
 * @param state - The live state (the player's ledger)
 * @param prompt - The prompt
 * @returns Whether a door opening it now would find nothing to ask
 */
const isPromptSpent = (state: RuntimeState, prompt: PromptDefinition): boolean =>
	!prompt.repeatable && state.narrative.askedPromptIds.includes(prompt.promptId);

/**
 * Ask a prompt — the one door every opener goes through (`talk`'s
 * `opensWith`, an option's `leadsTo`, the `ask_prompt` effect).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the ask
 * @param cursor - Where the question lands in the transcript
 * @param actorId - The actor asking
 * @param promptId - The prompt it asks
 * @returns The asking — declined (nothing spoken, state unchanged) when the actor is out of earshot, the prompt is spent, or nothing is offerable
 */
export const askPrompt = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor, actorId: ActorId, promptId: PromptId): PromptAsking => {
	const actor = requireActor(bundle, actorId);
	const prompt = requirePrompt(actor, promptId);
	const offered = offerableOptionsOf(bundle, state, prompt);

	if (!isActorAddressable(bundle, state, actorId) || isPromptSpent(state, prompt) || offered.length === 0) {
		return { cursor, entries: [], state };
	}

	const question: NarrativeLine = { priority: 'standard', source: voiceOf(actor), text: prompt.questionText };
	const spoken = stampNarrativeLines(bundle, state, cursor, [question], `prompt_asked:${promptId}`);
	const asked: RuntimeState = {
		...state,
		narrative: {
			...state.narrative,
			askedPromptIds: state.narrative.askedPromptIds.includes(promptId) ? state.narrative.askedPromptIds : [...state.narrative.askedPromptIds, promptId],
		},
	};
	const parked: PendingDialogueQuestion = {
		actorId,
		// The question's identity is the input that asked it (D79); the input
		// is already in the history when any turn runs.
		askedAtInputOrdinal: state.session.commandHistory.length,
		// A dialogue question has no asking clause: the words that answer it are the option's, never a splice.
		clauseText: '',
		kind: 'dialogue',
		options: offered.map((option) => ({
			label: option.label,
			optionId: option.optionId,
			words: [...new Set([option.label, ...option.aliases].map(foldCharacters))],
		})),
		promptId,
		// Resolved at park time, the R15 pattern: the face shows what the transcript said.
		questionText: resolveConditionalText(prompt.questionText, bundle, state),
		roomId: state.progression.activeRoomId,
	};

	// The one asking is the one the player is talking with (D235): its pronoun names it from here.
	return { cursor: spoken.cursor, entries: spoken.entries, state: parkQuestion(rememberConversationPartner(asked, actorId), parked) };
};

/**
 * The offered option the player's words name, if any — whole input, folded,
 * first in declared order, never fuzzy.
 *
 * @param question - The parked dialogue question
 * @param words - The player's input as the conversation reads it
 * @returns The answer chosen, or `undefined` when the words are not an answer (and so are a command)
 */
export const dialogueAnswerOf = (question: PendingDialogueQuestion, words: string): PendingDialogueQuestion['options'][number] | undefined => {
	const folded = foldCharacters(words);

	return folded === '' ? undefined : question.options.find((option) => option.words.includes(folded));
};

/**
 * The turn an answer makes: the option's lines, its effects, and the prompt
 * it leads on to.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn, the question already cleared
 * @param cursor - This turn's transcript cursor
 * @param question - The question answered
 * @param optionId - The answer given
 * @returns The completed turn, before the tick
 */
export const answerPrompt = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	question: PendingDialogueQuestion,
	optionId: PendingDialogueQuestion['options'][number]['optionId'],
): CompletedAction => {
	const actor = requireActor(bundle, question.actorId);
	const prompt = requirePrompt(actor, question.promptId);
	const option = prompt.options.find((candidate) => candidate.optionId === optionId);

	if (option === undefined) {
		throw new EngineInvariantError(`prompt "${question.promptId}" offers no option "${optionId}" — a parked question names only options its prompt declares`);
	}

	const spoken = stampNarrativeLines(bundle, state, cursor, option.narratives, `dialogue:${question.promptId}:${optionId}`);
	const changed = withEffects(bundle, { cursor: spoken.cursor, entries: spoken.entries, state, type: 'completed' }, option.effects);

	if (option.leadsTo === undefined) {
		return changed;
	}

	// The back-and-forth: the next question, in the same turn, through the same door.
	const led = askPrompt(bundle, changed.state, changed.cursor, question.actorId, option.leadsTo);

	return { ...changed, cursor: led.cursor, entries: [...changed.entries, ...led.entries], state: led.state };
};
